import { AnimatePresence } from 'framer-motion';
import { FC, useEffect, useMemo, useState } from 'react';

import { ActivityOverviewIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import { CrossfadePanel } from '../../components/motion/index';
import { Company, SnapshotHooks, useScreenSize } from '../../hooks/index';
import { Snapshot } from '../../types/snapshot.types';
import { CapTableSelect } from './cap-table-select';
import {
  CapTableDetailsPlaceholder,
  CapTableGraphPlaceholder,
  CapTableMobileDetailsPlaceholder,
  CapTableOverAllStatusPlaceholder,
  CapTableSelectPlaceholder,
} from './components/cap-table-loading-placeholders';
import { DesktopDetails } from './desktop-details/index';
import { CapTableGraphWrapper } from './graphs/index';
import { MobileDetails } from './mobile-details/index';
import { OverAllStatus } from './over-all-status';
import { GraphVariant } from './variables';

const CapTable: FC = () => {
  const { selectedCompanyId } = Company.useSelected();
  const [customSnapshotDate, setCustomSnapshotDate] = useState<Date | null>(null);
  const [selectedSnapshotSourceId, setSelectedSnapshotSourceId] = useState('');
  const [selectedGraphVariant, setGraphVariant] = useState(GraphVariant.OWNERSHIP);
  const { width } = useScreenSize();
  const [snapshot, setSnapshot] = useState<Snapshot | undefined>();

  const { snapshots, isLoading: isLoadingSnapshotList } = SnapshotHooks.useSnapshots({
    companyId: selectedCompanyId,
    capTableMode: true,
  });

  const {
    snapshot: currentSnapshot,
    isSuccess: isLoadingFinished,
    isLoading: isLoadingCurrent,
  } = SnapshotHooks.useCurrent({
    companyId: selectedCompanyId,
    capTableMode: true,
  });

  const { snapshot: snapshotByDate, isLoading: isLoadingSnapshotByDate } =
    SnapshotHooks.useSnapshotByDate({
      companyId: selectedCompanyId,
      date: customSnapshotDate,
      capTableMode: true,
    });

  const snapshotPanelKey = useMemo(
    () =>
      customSnapshotDate
        ? `date-${customSnapshotDate.toISOString()}`
        : selectedSnapshotSourceId
          ? `source-${selectedSnapshotSourceId}`
          : 'latest',
    [customSnapshotDate, selectedSnapshotSourceId],
  );

  // The list carries slim rows only — resolve the selected source to its snapshot id, then fetch
  // that one row's full stats by id.
  const selectedListRowId = useMemo(
    () =>
      selectedSnapshotSourceId && !customSnapshotDate
        ? (
            snapshots.find((item) => item.valuation?.id === selectedSnapshotSourceId) ??
            snapshots.find((item) => item.companyIncorporation?.id === selectedSnapshotSourceId)
          )?.id
        : undefined,
    [customSnapshotDate, selectedSnapshotSourceId, snapshots],
  );

  const { snapshot: selectedSnapshot, isLoading: isLoadingSelectedSnapshot } =
    SnapshotHooks.useSnapshot({
      companyId: selectedCompanyId,
      snapshotId: selectedListRowId ?? '',
      capTableMode: true,
    });

  useEffect(() => {
    if (customSnapshotDate) return setSnapshot(snapshotByDate);
    if (!selectedSnapshotSourceId) return setSnapshot(currentSnapshot);
    setSnapshot(selectedSnapshot);
  }, [
    currentSnapshot,
    customSnapshotDate,
    selectedSnapshot,
    selectedSnapshotSourceId,
    snapshotByDate,
  ]);

  const toggleGraphVariant = () => {
    setGraphVariant((prevVariant) =>
      prevVariant === GraphVariant.OWNERSHIP ? GraphVariant.VOTING_RIGHTS : GraphVariant.OWNERSHIP,
    );
  };

  const isDesktop = width >= 1344;
  const isTablet = width >= 1024 && width < 1344;
  // The snapshot list is deliberately NOT part of this gate: it is a page-sized list of full
  // snapshots (multi-MB on a heavy company) needed only to resolve a historical valuation, while
  // the latest cap table needs the current snapshot alone. Blocking on it kept the whole page on
  // a skeleton for seconds after the (cached) current snapshot had already arrived.
  const isPageLoading = isLoadingCurrent;
  const isSnapshotSwitching =
    Boolean(customSnapshotDate && isLoadingSnapshotByDate) ||
    Boolean(selectedSnapshotSourceId && (isLoadingSnapshotList || isLoadingSelectedSnapshot));
  const showOverviewPlaceholder = isPageLoading || isSnapshotSwitching;

  return (
    <div className="flex h-fit w-full flex-col gap-4 overflow-hidden p-1">
      <div className="flex items-center justify-between rounded-md p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <ActivityOverviewIcon />
          <span className="text-base font-semibold text-gray-700">
            <AppFormattedMessage id="OVERVIEW" />
          </span>
        </div>

        {isPageLoading ? (
          <CapTableSelectPlaceholder />
        ) : (
          <CapTableSelect
            companyId={selectedCompanyId}
            isSnapshotListLoading={isLoadingSnapshotList}
            onSelectCapTable={setSelectedSnapshotSourceId}
            onSelectCustomCapTableDate={setCustomSnapshotDate}
          />
        )}
      </div>
      <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
        <AnimatePresence mode="wait">
          {showOverviewPlaceholder ? (
            <CrossfadePanel
              className="h-full min-h-0"
              panelKey={`${snapshotPanelKey}-status-loading`}
            >
              <CapTableOverAllStatusPlaceholder wrapperClassName="h-full" />
            </CrossfadePanel>
          ) : (
            <CrossfadePanel className="h-full min-h-0" panelKey={`${snapshotPanelKey}-status`}>
              <OverAllStatus
                isLoadingFinished={isLoadingFinished}
                snapshot={snapshot}
                snapshotPanelKey={snapshotPanelKey}
                wrapperClassName="h-full"
              />
            </CrossfadePanel>
          )}
        </AnimatePresence>
        <AnimatePresence mode="wait">
          {showOverviewPlaceholder ? (
            <CrossfadePanel
              className="h-full min-h-0"
              panelKey={`${snapshotPanelKey}-graph-loading`}
            >
              <CapTableGraphPlaceholder
                isDesktop={isDesktop}
                isTablet={isTablet}
                wrapperClassName="h-full"
              />
            </CrossfadePanel>
          ) : (
            <CrossfadePanel
              className="h-full min-h-0"
              delay={0.05}
              panelKey={`${snapshotPanelKey}-graph`}
            >
              <CapTableGraphWrapper
                isDesktop={isDesktop}
                isLoadingFinished={isLoadingFinished}
                isTablet={isTablet}
                selectedGraphVariant={selectedGraphVariant}
                snapshot={snapshot}
                snapshotPanelKey={snapshotPanelKey}
                toggleGraphVariant={toggleGraphVariant}
                wrapperClassName="h-full"
              />
            </CrossfadePanel>
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence mode="wait">
        {showOverviewPlaceholder ? (
          <CrossfadePanel
            className="max-lg:hidden"
            panelKey={`${snapshotPanelKey}-details-loading`}
          >
            <CapTableDetailsPlaceholder className="max-lg:hidden" />
          </CrossfadePanel>
        ) : (
          <CrossfadePanel
            className="max-lg:hidden"
            delay={0.1}
            panelKey={`${snapshotPanelKey}-details`}
          >
            <DesktopDetails
              className="max-lg:hidden"
              companyId={selectedCompanyId}
              isLoadingFinished={isLoadingFinished}
              snapshot={snapshot}
              snapshotPanelKey={snapshotPanelKey}
            />
          </CrossfadePanel>
        )}
      </AnimatePresence>
      <AnimatePresence mode="wait">
        {showOverviewPlaceholder ? (
          <CrossfadePanel className="lg:hidden" panelKey={`${snapshotPanelKey}-mobile-loading`}>
            <CapTableMobileDetailsPlaceholder className="lg:hidden" />
          </CrossfadePanel>
        ) : (
          <CrossfadePanel className="lg:hidden" delay={0.1} panelKey={`${snapshotPanelKey}-mobile`}>
            <MobileDetails
              className="lg:hidden"
              snapshot={snapshot}
              snapshotPanelKey={snapshotPanelKey}
            />
          </CrossfadePanel>
        )}
      </AnimatePresence>
    </div>
  );
};

CapTable.displayName = 'CapTable';

export default CapTable;
